// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import api from '../api/client'
import toast from 'react-hot-toast'
import { confirmDialog } from '../components/DialogHost'
import { Key, Copy, RefreshCw, Monitor, CheckCircle, XCircle, Loader2 } from 'lucide-react'

interface License {
  id: string
  license_key: string
  license_type: string
  max_users: number
  max_endpoints: number
  max_vulnerabilities: number
  is_active: boolean
  expires_at: string | null
  days_until_expiry: number | null
  created_at: string
  installation_count: number
}

interface Installation {
  id: string
  installation_id: string
  hostname: string | null
  ip_address: string | null
  platform: string | null
  current_version: string | null
  is_active: boolean
  is_online: boolean
  registered_at: string
  last_heartbeat: string | null
}

// Helper to truncate license key for display
const truncateLicenseKey = (key: string, showChars: number = 8): string => {
  if (!key || key.length <= showChars * 2 + 3) return key
  return `${key.slice(0, showChars)}...${key.slice(-showChars)}`
}

export default function Licenses() {
  const [licenses, setLicenses] = useState<License[]>([])
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null)
  const [installations, setInstallations] = useState<Installation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingInstallations, setIsLoadingInstallations] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)

  useEffect(() => {
    loadLicenses()
  }, [])

  const loadLicenses = async () => {
    try {
      const res = await api.get('/api/portal/licenses')
      setLicenses(res.data)
      if (res.data.length > 0) {
        setSelectedLicense(res.data[0])
        loadInstallations(res.data[0].id)
      }
    } catch (error) {
      console.error('Failed to load licenses:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadInstallations = async (licenseId: string) => {
    setIsLoadingInstallations(true)
    try {
      const res = await api.get(`/api/portal/licenses/${licenseId}/activations`)
      setInstallations(res.data)
    } catch (error) {
      console.error('Failed to load installations:', error)
    } finally {
      setIsLoadingInstallations(false)
    }
  }

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key)
    toast.success('License key copied to clipboard')
  }

  const handleRegenerate = async () => {
    if (!selectedLicense) return
    if (!(await confirmDialog({
      title: 'Regenerate license key',
      message: 'Are you sure you want to regenerate this license key? You will need to update all installations.',
      confirmText: 'Regenerate',
      danger: true,
    }))) {
      return
    }

    setIsRegenerating(true)
    try {
      await api.post(`/api/portal/licenses/${selectedLicense.id}/regenerate`)
      toast.success('License key regenerated!')
      loadLicenses()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate key')
    } finally {
      setIsRegenerating(false)
    }
  }

  const handleRemove = async (installationId: string) => {
    if (!selectedLicense) return
    if (!(await confirmDialog({ title: 'Remove installation', message: 'Permanently remove this installation record? This frees the license seat and cannot be undone.', confirmText: 'Remove', danger: true }))) return

    try {
      await api.delete(`/api/portal/licenses/${selectedLicense.id}/activations/${installationId}`)
      toast.success('Installation removed')
      loadInstallations(selectedLicense.id)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to remove installation')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  if (licenses.length === 0) {
    return (
      <div className="text-center py-12">
        <Key className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">No Licenses</h2>
        <p className="text-gray-400">Subscribe to a plan to get your license key.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 fade-in">
      <h1 className="text-2xl font-bold">Licenses</h1>

      {/* License Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {licenses.map((license) => (
          <div
            key={license.id}
            onClick={() => {
              setSelectedLicense(license)
              loadInstallations(license.id)
            }}
            className={`card cursor-pointer transition-colors ${
              selectedLicense?.id === license.id ? 'border-primary-500' : 'hover:border-gray-600'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-primary-500" />
                <span className="font-semibold capitalize">{license.license_type}</span>
              </div>
              <span className={`badge ${license.is_active ? 'badge-success' : 'badge-danger'}`}>
                {license.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="space-y-1 text-sm text-gray-400">
              <p>{license.max_endpoints.toLocaleString()} endpoints</p>
              <p>{license.max_users} users</p>
              <p>{license.installation_count} installation(s)</p>
            </div>
          </div>
        ))}
      </div>

      {/* Selected License Details */}
      {selectedLicense && (
        <>
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">License Key</h2>
            <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
              <code className="flex-1 text-sm text-primary-500 font-mono" title={selectedLicense.license_key}>
                {truncateLicenseKey(selectedLicense.license_key)}
              </code>
              <button
                onClick={() => handleCopyKey(selectedLicense.license_key)}
                className="btn btn-secondary"
                title="Copy to clipboard"
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating}
                className="btn btn-secondary"
                title="Regenerate key"
              >
                {isRegenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Keep this key secure. If compromised, regenerate it immediately.
            </p>
          </div>

          {/* License Limits */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">License Limits</h2>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-400">Max Users</p>
                <p className="text-2xl font-bold">{selectedLicense.max_users.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Max Endpoints</p>
                <p className="text-2xl font-bold">{selectedLicense.max_endpoints.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Max Vulnerabilities</p>
                <p className="text-2xl font-bold">{selectedLicense.max_vulnerabilities.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Installations */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Installations</h2>
            {isLoadingInstallations ? (
              <div className="flex justify-center py-8">
                <div className="spinner"></div>
              </div>
            ) : installations.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Hostname</th>
                    <th>IP Address</th>
                    <th>Platform</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Last Seen</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {installations.map((inst) => (
                    <tr key={inst.id}>
                      <td className="font-medium">{inst.hostname || 'Unknown'}</td>
                      <td>{inst.ip_address || '-'}</td>
                      <td>{inst.platform || '-'}</td>
                      <td>{inst.current_version || '-'}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {inst.is_online ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-500" />
                          )}
                          <span className={inst.is_online ? 'text-green-500' : 'text-gray-500'}>
                            {inst.is_online ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </td>
                      <td className="text-gray-400">
                        {inst.last_heartbeat ? new Date(inst.last_heartbeat).toLocaleString() : 'Never'}
                      </td>
                      <td>
                        <button
                          onClick={() => handleRemove(inst.id)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Monitor className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No installations yet</p>
                <p className="text-sm">Use your license key to activate Exploit Score</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
